import { memo, useMemo } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { DisaggregatedMetric } from "../../services/types";
import { addCommas } from "../../utils/helpers";

interface RaceEthnicityMetadataChartProps {
  indicators: DisaggregatedMetric[] | null;
  title: string;
  metricId: number;
  height: string;
  barColor: string;
  reverseOrder: boolean;
  sort?: string;
}

type ChartRow = {
  label: string;
  value: number;
  rawValue: number | string;
  originalIndex: number;
};

const wrapLabel = (label: string, maxCharsPerLine = 22): string[] => {
  if (!label) return [""];
  const words = label.split(" ");
  const lines: string[] = [];
  let currentLine = "";

  for (const word of words) {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    if (testLine.length > maxCharsPerLine) {
      if (currentLine) lines.push(currentLine);
      currentLine = word;
    } else {
      currentLine = testLine;
    }
  }

  if (currentLine) lines.push(currentLine);
  return lines;
};

const CustomYAxisTick = (props: any) => {
  const { x, y, payload } = props;
  const lines = wrapLabel(payload.value, 32);

  return (
    <g transform={`translate(${x},${y})`}>
      {lines.map((line, index) => (
        <text
          key={`${line}-${index}`}
          x={0}
          y={index * 12}
          dy={4}
          textAnchor="end"
          fill="#3A2E29"
          fontSize={10}
        >
          {line}
        </text>
      ))}
    </g>
  );
};

const CustomValueLabel = (props: any) => {
  const { x, y, width, height, value } = props;

  if (value === null || value === undefined || value === "") return null;
  const labelX = width > 0 ? x + width + 6 : x + 6;

  return (
    <text
      x={labelX}
      y={y + height / 2}
      dy={4}
      fill="#5E4F4A"
      fontSize={12}
      fontWeight="bold"
      textAnchor="start"
    >
      {value}
    </text>
  );
};

const CustomTooltip = ({ active, payload }: any) => {
  if (!active || !payload || !payload.length) return null;

  const row = payload[0].payload;
  return (
    <div className="rounded-md bg-[#3A2E29] px-3 py-2 text-xs text-white shadow-md">
      <div className="font-semibold">{row.label}</div>
      <div>{row.tooltipLabel}</div>
    </div>
  );
};

const RaceEthnicityMetadataChart = memo(
  ({
    indicators,
    metricId,
    height,
    barColor,
    reverseOrder,
    sort,
  }: RaceEthnicityMetadataChartProps) => {
    const chartRows = useMemo(() => {
      if (!indicators) return [];

      const indicatorData = indicators.find((d) => d.id === metricId);
      if (!indicatorData) return [];

      const version = indicatorData.versions[0];
      if (!version) return [];

      const tallyMap = new Map<string, number>();
      const rawValueMap = new Map<string, number | string>();

      version.disaggregations.forEach((d) => {
        let percentage = 0;
        let rawValue: number | string = 0;

        const topLevelDenominator = version.percent_calc?.d;
        const disaggDenominator = d.percent_calc?.d;
        const disaggNumerator = d.percent_calc?.n;

        switch (version.unit) {
          case "integer":
            if (
              disaggDenominator === undefined ||
              topLevelDenominator === undefined
            ) {
              percentage = version.value ? d.value / version.value : 0;
              rawValue = d.value;
            } else if (
              topLevelDenominator !== disaggDenominator &&
              disaggNumerator !== undefined
            ) {
              percentage =
                disaggDenominator !== 0
                  ? disaggNumerator / disaggDenominator
                  : 0;
              rawValue = disaggNumerator;
            } else {
              percentage = version.value ? d.value / version.value : 0;
              rawValue = d.value;
            }
            break;

          case "percentage":
            percentage = Math.min(d.value, 1);
            rawValue =
              disaggNumerator !== undefined ? disaggNumerator : d.value;
            break;

          case "decimal":
          case "ratio":
            percentage = d.value;
            rawValue = d.value;
            break;

          default:
            percentage = 0;
            rawValue = "0";
        }

        tallyMap.set(d.name.trim(), percentage);
        rawValueMap.set(d.name.trim(), rawValue);
      });

      let rows: ChartRow[] = Array.from(tallyMap.entries()).map(
        ([name, percentage], index) => ({
          label: name,
          value: percentage,
          rawValue: rawValueMap.get(name) ?? 0,
          originalIndex: index,
        })
      );

      if (sort === "alphabetical") {
        rows = rows.sort((a, b) => a.label.localeCompare(b.label));
      }

      if (reverseOrder) {
        rows.reverse();
      }

      return rows.map((row) => {
        const currentIndicator = indicators.find((d) => d.id === metricId);
        const version = currentIndicator?.versions[0];
        const unit = version?.unit;
        const disaggregation = version?.disaggregations.find(
          (d) => d.name.trim() === row.label.trim()
        );

        let displayLabel = "";

        if (unit === "ratio") {
          const numerator = Number(disaggregation?.percent_calc?.n);
          const denominator = Number(disaggregation?.percent_calc?.d);
          displayLabel =
            denominator && !Number.isNaN(numerator / denominator)
              ? (numerator / denominator).toFixed(2)
              : "N/A";
        } else {
          const numerator = disaggregation?.percent_calc?.n;
          const denominator = disaggregation?.percent_calc?.d;

          if (typeof numerator !== "number" || typeof denominator !== "number") {
            displayLabel = "";
          } else if (denominator === 0) {
            displayLabel = "N/A";
          } else {
            displayLabel = `${((numerator / denominator) * 100).toFixed(0)}%`;
          }
        }

        let tooltipLabel = "";
        const versionValue = currentIndicator?.versions[0]?.value;
        const rawValue = row.rawValue;
        const originalDisagg =
          currentIndicator?.versions[0]?.disaggregations[row.originalIndex];
        const n = originalDisagg?.percent_calc?.n;
        const d = originalDisagg?.percent_calc?.d;

        if (Number(currentIndicator?.id) === 1072) {
          tooltipLabel = `${addCommas(Number(n))} of ${addCommas(
            Number(versionValue)
          )}`;
        } else if (n !== undefined && d !== undefined) {
          tooltipLabel =
            d === 0
              ? "No data (0 of 0)"
              : `${addCommas(Number(n))} of ${addCommas(Number(d))}`;
        } else if (Number(rawValue) <= 1 && unit === "percentage") {
          tooltipLabel = `${(Number(rawValue) * 100).toFixed(0)}%`;
        } else {
          tooltipLabel = `${addCommas(Number(rawValue))} of ${addCommas(
            Number(versionValue)
          )}`;
        }

        return {
          ...row,
          displayLabel,
          tooltipLabel,
          fill: barColor,
        };
      });
    }, [indicators, metricId, sort, reverseOrder, barColor]);

    const maxValue =
      chartRows.length > 0 ? Math.max(...chartRows.map((d) => d.value), 0) * 2 : 1;

    if (!chartRows.length) {
      return (
        <div className="w-full max-w-md">
          <div className={height || "h-80"} />
        </div>
      );
    }

    return (
      <div className="w-full max-w-md">
        <div className={height || "h-80"}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartRows}
              layout="vertical"
              margin={{ top: 8, right: 20, bottom: 8, left: 30 }}
              barCategoryGap={20}
            >
              <CartesianGrid
                strokeDasharray="0"
                horizontal={false}
                vertical={false}
              />
              <XAxis
                type="number"
                domain={[0, maxValue]}
                tick={false}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                type="category"
                dataKey="label"
                width={140}
                tickLine={false}
                axisLine={false}
                interval={0}
                tick={<CustomYAxisTick />}
              />
              <Tooltip
                content={<CustomTooltip />}
                cursor={{ fill: "rgba(0,0,0,0.04)" }}
              />
              <Bar
                dataKey="value"
                barSize={12}
                minPointSize={1}
                radius={[0, 0, 0, 0]}
                isAnimationActive={false}
                activeBar={{ stroke: "none" }}
              >
                {chartRows.map((entry, index) => (
                    <rect key={index} fill={entry.fill} />
                ))}
                <LabelList dataKey="displayLabel" content={<CustomValueLabel />} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  }
);

export default RaceEthnicityMetadataChart;